import { Subject, takeUntil } from 'rxjs';
import { LoaderComponent } from '~shared/components/loader/loader.component';
import { User } from '~shared/models/general/user.model';
import { TechnicalRole } from '~shared/models/general/role.enum';
import { UserApiService } from '~shared/services/api/general/user.api-service';
import { PuToastrService } from '~shared/services/toastr/pu-toastr.service';
import { USER_CACHE_STORAGE_KEY } from '~shared/services/auth/auth-storage.constants';
import { userCacheStorage } from '~shared/services/auth/auth-storage.util';

export class UserUtils {
  /**
   * @method getUsersFavoritesViews
   * @description Récupère les vues favorites de l'utilisateur
   * @param sectionType
   * @returns favoriteViews
   */
  public static getUsersFavoritesViews(sectionType: string) {
    const user = userCacheStorage().getItem(USER_CACHE_STORAGE_KEY);
    if (
      user &&
      JSON.parse(user).preference &&
      JSON.parse(user).preference[0] &&
      Array.isArray(JSON.parse(user).preference[0].views)
    ) {
      // Le repli vaut aussi quand AUCUNE entrée ne porte cette section : un
      // utilisateur ayant des favoris ailleurs recevait `undefined`, et
      // l'appelant, qui lit aussitôt `resultat[sectionType]`, levait au lieu de
      // rediriger.
      return (
        JSON.parse(user).preference[0].views.find(
          (x: any) => x[sectionType]
        ) ?? { [sectionType]: [] }
      );
    }
    return { [sectionType]: [] };
  }

  /**
   * @method updateUserPreferences
   * @description Metes à jour les vues favorites de l'utilisateur
   * @param decodedUser
   * @param uid
   * @returns void
   */
  public static updateUserPreferences(
    decodedUser: any,
    uid: number,
    loader: LoaderComponent,
    userApiService: UserApiService,
    toastr: PuToastrService,
    destroy$: Subject<void>
  ) {
    const params = new URLSearchParams();
    params.append('preference', JSON.stringify(decodedUser.preference));
    userApiService
      .update(uid, params)
      .pipe(takeUntil(destroy$))
      .subscribe(
        () => {
          userCacheStorage().setItem(
            USER_CACHE_STORAGE_KEY,
            JSON.stringify(decodedUser)
          );
          toastr.showPreferenceUpdateMessage();
        },
        (error) => {
          toastr.showErrorMessage(error, loader);
        }
      );
  }

  /**
   * @method initDecodedUser
   * @description Initialise l'utilisateur décodé
   * @param user
   * @returns decodedUser
   */
  public static initDecodedUser(user: string) {
    const decodedUser = JSON.parse(user);
    if (decodedUser && !decodedUser?.preference) {
      decodedUser.preference = [{ views: [] }];
    }
    return decodedUser;
  }

  /**
   * Récupère les IDs des vues favorites de l'utilisateur (clé `fav_views`).
   * Distinct des favoris de records gérés par sectionType.
   */
  public static getFavoriteViewIds(): number[] {
    const user = userCacheStorage().getItem(USER_CACHE_STORAGE_KEY);
    if (!user) return [];
    try {
      const userObj = JSON.parse(user);
      const views = userObj?.preference?.[0]?.views;
      if (!Array.isArray(views)) return [];
      const entry = views.find((x: any) => Array.isArray(x?.fav_views));
      return entry?.fav_views ?? [];
    } catch {
      return [];
    }
  }

  /**
   * Sauvegarde la liste complète des IDs de vues favorites.
   * Met à jour localStorage et persiste via UserApiService.
   */
  public static saveFavoriteViewIds(
    ids: number[],
    uid: number,
    loader: LoaderComponent,
    userApiService: UserApiService,
    toastr: PuToastrService,
    destroy$: Subject<void>
  ): void {
    const user = userCacheStorage().getItem(USER_CACHE_STORAGE_KEY);
    if (!user) return;
    const decodedUser = UserUtils.initDecodedUser(user);
    if (!Array.isArray(decodedUser.preference[0].views)) {
      decodedUser.preference[0].views = [];
    }
    const views = decodedUser.preference[0].views;
    const idx = views.findIndex((x: any) => Array.isArray(x?.fav_views));
    if (idx >= 0) {
      views[idx] = { fav_views: ids };
    } else {
      views.push({ fav_views: ids });
    }
    UserUtils.updateUserPreferences(
      decodedUser,
      uid,
      loader,
      userApiService,
      toastr,
      destroy$
    );
  }

  /**
   * @method isUserAdmin
   * @description détecte si le user est admin
   * @param user User
   * @returns boolean
   */
  public static isUserAdmin(user: User): boolean {
    return Array.isArray(user?.roles)
      ? user.roles.includes(TechnicalRole.CLIENT_ADMIN)
      : false;
  }

  /**
   * @method getUserInitials
   * @description Récupère les initiales de l'utilisateur
   * @param user User
   * @returns string
   */
  public static getUserInitials(user: User): string {
    if (!user || !user.first_name || !user.last_name) {
      return '';
    }
    const firstInitial = user.first_name.charAt(0).toUpperCase();
    const lastInitial = user.last_name.charAt(0).toUpperCase();
    return `${firstInitial}${lastInitial}`;
  }

  /**
   * @description Récupère le nom complet de l'utilisateur
   * @param user User
   * @returns
   */
  public static getUserFullName(user: User): string {
    if (!user || !user.first_name || !user.last_name) {
      return '';
    }
    return `${user.first_name} ${user.last_name}`;
  }

  /**
   * @description Récupère l'email de l'utilisateur
   * @param user User
   * @returns
   */
  public static getUserEmail(user: User): string {
    if (!user || !user.email) {
      return '';
    }
    return user.email;
  }
}
